import { Pressable, View } from "@-/com.npmjs.react-native";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import type { MorphemeDetailsTileSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { toDiacriticPinyin } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin";

/**
 * One search result: the morpheme, its readings and its gloss. Deliberately context-free (unlike
 * the Search tab's `MorphemeDetailsRow`, which joins the app layout's selection mode), so the sheet
 * works from any surface, including overlays mounted above the app layout.
 */
export function MorphemeSearchSheetResultRow({
  morpheme,
  onPress,
}: {
  morpheme: MorphemeDetailsTileSlice;
  onPress: (morpheme: MorphemeDetailsTileSlice) => void;
}) {
  const pinyin = morpheme.pinyinList
    .map(({ toneNumbersPinyin }) => toDiacriticPinyin(toneNumbersPinyin))
    .join(", ");
  // `||`: an empty gloss falls through to the first reading's, as the Search tab's rows do.
  const gloss = morpheme.back || morpheme.pinyinList[0]?.gloss || "";
  return (
    <Pressable
      accessibilityLabel={[morpheme.front, pinyin, gloss].filter(Boolean).join(", ")}
      accessibilityRole="button"
      className="flex-row items-center gap-3 border-b border-border py-3 active:bg-accent"
      onPress={() => onPress(morpheme)}
      testID={`morpheme-search-sheet-result-${morpheme.front}`}
    >
      <Text className="min-w-10 text-3xl text-foreground">{morpheme.front}</Text>
      <View className="min-w-0 flex-1">
        <Text className="text-sm text-muted-foreground" numberOfLines={1}>
          {pinyin || "-"}
        </Text>
        <Text className="text-base text-foreground" numberOfLines={1}>
          {gloss || "-"}
        </Text>
      </View>
    </Pressable>
  );
}
